import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, History, Power, PowerOff } from "lucide-react";

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/pricing-catalog").then(({ data }) => {
    setData(data);
    setDraft(JSON.parse(JSON.stringify(data.active)));
  }).catch(() => toast.error("Could not load pricing catalog"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data || !draft) return <div className="py-16 text-center text-slate-400" data-testid="pricing-loading">Loading pricing…</div>;

  const setTier = (k, field, v) => setDraft({ ...draft, service_tiers: { ...draft.service_tiers, [k]: { ...draft.service_tiers[k], [field]: field === "price" || field === "included_attempts" ? Number(v) : v } } });
  const setAddon = (k, v) => setDraft({ ...draft, addons: { ...draft.addons, [k]: { ...draft.addons[k], price: Number(v) } } });
  const setPrint = (field, v) => setDraft({ ...draft, printing: { ...draft.printing, [field]: Number(v) } });

  const saveNewVersion = async () => {
    setBusy(true);
    try {
      await api.put("/admin/pricing-catalog", {
        currency: draft.currency, service_tiers: draft.service_tiers, addons: draft.addons,
        printing: draft.printing, server_share_pct: Number(draft.server_share_pct),
      });
      toast.success("New pricing version created and activated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (version, active) => {
    try { await api.post(`/admin/pricing-catalog/${version}/status`, { active }); toast.success(`Version ${version} ${active ? "activated" : "deactivated"}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="admin-pricing-page">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Pricing Catalog</h2>
        <p className="text-slate-500">Active version <strong>v{data.active.version}</strong> · server share {data.active.server_share_pct}% · historical orders keep their original snapshots.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
        <h3 className="font-display text-lg font-bold text-slate-900">Service tiers</h3>
        {Object.entries(draft.service_tiers).map(([k, t]) => (
          <div key={k} className="grid gap-3 border-b border-slate-100 pb-4 sm:grid-cols-4" data-testid={`tier-${k}`}>
            <div className="sm:col-span-2"><Label>{t.label} ({k})</Label><Input value={t.label} onChange={(e) => setTier(k, "label", e.target.value)} /></div>
            <div><Label>Price</Label><Input data-testid={`tier-price-${k}`} type="number" value={t.price} onChange={(e) => setTier(k, "price", e.target.value)} /></div>
            <div><Label>Included attempts</Label><Input type="number" value={t.included_attempts} onChange={(e) => setTier(k, "included_attempts", e.target.value)} /></div>
          </div>
        ))}

        <h3 className="font-display text-lg font-bold text-slate-900 pt-2">Add-ons</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(draft.addons).map(([k, a]) => (
            <div key={k} className="flex items-center gap-3" data-testid={`addon-${k}`}>
              <Label className="flex-1 text-sm text-slate-600">{a.label}</Label>
              <Input className="w-28" type="number" value={a.price} onChange={(e) => setAddon(k, e.target.value)} />
            </div>
          ))}
        </div>

        <h3 className="font-display text-lg font-bold text-slate-900 pt-2">Printing & compensation</h3>
        <div className="grid gap-3 sm:grid-cols-4">
          <div><Label>Tier1 max page</Label><Input type="number" value={draft.printing.tier1_max_page} onChange={(e) => setPrint("tier1_max_page", e.target.value)} /></div>
          <div><Label>Rate 1–{draft.printing.tier1_max_page}</Label><Input type="number" step="0.01" value={draft.printing.tier1_rate} onChange={(e) => setPrint("tier1_rate", e.target.value)} /></div>
          <div><Label>Rate {draft.printing.tier1_max_page + 1}+</Label><Input type="number" step="0.01" value={draft.printing.tier2_rate} onChange={(e) => setPrint("tier2_rate", e.target.value)} /></div>
          <div><Label>Server share %</Label><Input data-testid="server-share-pct" type="number" value={draft.server_share_pct} onChange={(e) => setDraft({ ...draft, server_share_pct: Number(e.target.value) })} /></div>
        </div>

        <Button data-testid="save-pricing-version" disabled={busy} onClick={saveNewVersion} className="bg-slate-900 text-white hover:bg-slate-800">
          <Save className="mr-1.5 h-4 w-4" /> {busy ? "Saving…" : "Save as new version"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><History className="h-4 w-4" /> Version history</h3>
        <div className="mt-3 space-y-2">
          {data.history.map((h) => (
            <div key={h.version} data-testid={`pricing-version-${h.version}`} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span className="text-slate-700">v{h.version} · <span className={h.status === "active" ? "font-semibold text-emerald-600" : "text-slate-400"}>{h.status}</span> · by {h.created_by || "system"}</span>
              {h.status === "active" ? (
                <Button size="sm" variant="outline" data-testid={`deactivate-${h.version}`} onClick={() => setStatus(h.version, false)}><PowerOff className="mr-1 h-3.5 w-3.5" /> Deactivate</Button>
              ) : (
                <Button size="sm" variant="outline" data-testid={`activate-${h.version}`} onClick={() => setStatus(h.version, true)}><Power className="mr-1 h-3.5 w-3.5" /> Activate</Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900">Audit history</h3>
        <div className="mt-3 space-y-1 text-sm text-slate-600" data-testid="pricing-audit">
          {(data.audit || []).map((a, i) => (
            <div key={i} className="flex justify-between border-b border-slate-100 py-1">
              <span>{a.action} · v{a.version}</span><span className="text-slate-400">{a.by} · {a.at ? new Date(a.at).toLocaleString() : ""}</span>
            </div>
          ))}
          {(!data.audit || data.audit.length === 0) && <p className="text-slate-400">No audit entries yet.</p>}
        </div>
      </div>
    </div>
  );
}
